import { ARENAS } from '../shared/maps';
import { BOT_PROFILES } from '../shared/ai';
import { playableRoom } from '../shared/expedition';
import { Sim } from '../shared/sim';
import { isProgress, isProgressOperation } from '../shared/progression';
import { DEFAULT_PREFS } from './store';
import { PROGRESS_PREFIX, Progression } from './progression';

interface Backup { version: 1; id: string; origin: string; savedAt: string; entries: Record<string, string> }
const allowed = (key: string) => key === 'bg9.prefs.v1' || key === 'bg9.run.v1' || key.startsWith(PROGRESS_PREFIX);
const el = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const status = el('status');
const canonical = location.hostname === 'detonarium.com' || location.hostname === 'localhost' || location.hostname === '127.0.0.1';
const localEntries = (): Record<string, string> => Object.fromEntries(Object.keys(localStorage).filter(allowed).map(k => [k, localStorage.getItem(k)!]));
const backup = (): Backup => ({ version: 1, id: crypto.randomUUID(), origin: location.origin, savedAt: new Date().toISOString(), entries: localEntries() });
function download(data: Backup, label: string): void {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data)], { type: 'application/json' }));
  const a = document.createElement('a'); a.href = url; a.download = `detonarium-${label}-${Date.now()}.json`; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
function describe(entries: Record<string, string>): string {
  const prefs = JSON.parse(entries['bg9.prefs.v1'] || '{}');
  const run = JSON.parse(entries['bg9.run.v1'] || 'null');
  return `${prefs.salvage ?? 0} salvage, ${prefs.unlocks?.length ?? 0} unlocks, ${run ? `parked run at room ${run.index + 1}` : 'no parked run'}.`;
}

/** Import only game saves, never cookies, sessions, tokens, or arbitrary storage keys. */
function validate(raw: unknown): Backup {
  const b = raw as Backup;
  if (!b || b.version !== 1 || !b.entries || typeof b.entries !== 'object' || Array.isArray(b.entries)) throw new Error('This is not a Detonarium save file.');
  for (const [key, value] of Object.entries(b.entries)) {
    if (!allowed(key) || typeof value !== 'string' || value.length > 2_000_000) throw new Error('The file contains an unsupported save entry.');
    if (key === 'bg9.prefs.v1') {
      const p = JSON.parse(value);
      if (!p || typeof p !== 'object' || Array.isArray(p)) throw new Error('Invalid preferences.');
      for (const [k, v] of Object.entries(p)) {
        const example = DEFAULT_PREFS[k as keyof typeof DEFAULT_PREFS];
        if (!Object.hasOwn(DEFAULT_PREFS, k) || (example !== null && typeof v !== typeof example) ||
          (typeof v === 'number' && (!Number.isFinite(v) || Math.abs(v) > 1_000_000)) ||
          (typeof v === 'string' && v.length > 200)) throw new Error(`Invalid preference: ${k}`);
      }
      const options: Record<string, unknown[]> = { quality: ['auto', 'low', 'medium', 'high', 'ultra'],
        autoTier: [null, 'low', 'medium', 'high', 'ultra'], autoCeiling: [null, 'low', 'medium', 'high', 'ultra'],
        hdr: ['auto', 'on', 'off'], cameraMode: ['arena', 'follow', 'auto'], mode: ['ffa', 'teams', 'survival'],
        winBy: ['lives', 'kills', 'timed'], arena: ARENAS.map(a => a.id) };
      for (const [key, choices] of Object.entries(options)) {
        if (key in p && !choices.includes(p[key])) throw new Error(`Invalid preference: ${key}`);
      }
      const limits: Record<string, [number, number]> = { slot: [0, 7], bots: [0, 7], botLevel: [0, BOT_PROFILES.length - 1],
        lives: [1, 99], roundSeconds: [1, 36000], hitPoints: [1, 100], killTarget: [1, 10000] };
      for (const [key, [min, max]] of Object.entries(limits)) {
        if (key in p && (!Number.isInteger(p[key]) || p[key] < min || p[key] > max)) throw new Error(`Invalid preference: ${key}`);
      }
      if (!isProgress({ salvage: p.salvage ?? 0, unlocks: p.unlocks ?? [], bestRoom: p.bestExpedition ?? 0,
        runs: p.expeditionRuns ?? 0, clears: p.expeditionClears ?? 0 })) throw new Error('Invalid progression.');
    } else if (key === 'bg9.run.v1') {
      const r = JSON.parse(value);
      if (!r || !Array.isArray(r.rooms) || !r.rooms.length || r.rooms.length > 64 || !r.rooms.every(playableRoom) ||
        !Number.isInteger(r.index) || r.index < 0 || r.index >= r.rooms.length ||
        !Array.isArray(r.choices) || r.choices.length > 3 || !r.choices.every(playableRoom) ||
        (r.checkpoint && !Sim.restore(r.checkpoint))) throw new Error('The saved run cannot be resumed by this version.');
    } else if (key.endsWith('.base')) {
      const v = JSON.parse(value);
      if (!isProgress(v.progress) || !Number.isInteger(v.revision) || !Array.isArray(v.acknowledged)) throw new Error('Invalid account save.');
    } else if (key.includes('.op.')) {
      const v = JSON.parse(value);
      if (!isProgressOperation(v.operation) || typeof v.done !== 'boolean' || !Number.isFinite(v.at)) throw new Error('Invalid queued progress.');
    } else if (key !== PROGRESS_PREFIX + 'active' && key !== PROGRESS_PREFIX + 'initialized') throw new Error('Unknown progression entry.');
  }
  return b;
}

el('intro').textContent = canonical ? 'Restore a save downloaded from the old game address. Account progress will sync after you open the game; guest sign-in does not transfer between browsers.' :
  'The game moved to detonarium.com. Browser saves stay with their original address. Download your save here, then restore that file at Detonarium.';
el('import-section').hidden = !canonical;
el<HTMLAnchorElement>('continue').href = canonical ? '/' : 'https://detonarium.com/api/recover';
let selected: Backup | null = null;
try {
  const entries = localEntries();
  el('summary').textContent = describe(entries);
  if (!canonical && !Object.keys(entries).length) location.replace('https://detonarium.com/');
} catch { status.textContent = 'This browser is blocking access to saved data. Allow storage for this address and reload.'; }
el('export').onclick = () => {
  try { download(backup(), 'save'); status.textContent = 'Save downloaded. Keep the file until you have checked the restored game.'; }
  catch { status.textContent = 'Could not read or download this browser’s save.'; }
};
el<HTMLInputElement>('file').onchange = async () => {
  selected = null; el<HTMLButtonElement>('import').disabled = true;
  el<HTMLInputElement>('confirm').checked = false; el('confirm-row').hidden = true;
  try {
    const file = el<HTMLInputElement>('file').files?.[0];
    if (!file) return;
    if (file.size > 5_000_000) throw new Error('Save files must be smaller than 5 MB.');
    selected = validate(JSON.parse(await file.text()));
    el('preview').textContent = 'Selected save: ' + describe(selected.entries) + '\nCurrent save: ' + describe(localEntries());
    el('confirm-row').hidden = false; status.textContent = '';
  } catch (error) { status.textContent = (error as Error).message; }
};
el<HTMLInputElement>('confirm').onchange = () => { el<HTMLButtonElement>('import').disabled = !selected || !el<HTMLInputElement>('confirm').checked; };
el('import').onclick = () => {
  if (!selected || !el<HTMLInputElement>('confirm').checked) return;
  const previous = backup();
  try {
    download(previous, 'before-restore');
    // Retain a rollback copy even when the browser blocks a second download.
    localStorage.setItem('bg9.recovery.backup', JSON.stringify(previous));
    for (const key of Object.keys(previous.entries)) localStorage.removeItem(key);
    for (const [key, value] of Object.entries(selected.entries)) localStorage.setItem(key, value);
    // Guest cookies do not transfer between origins. Convert the restored
    // browser balance into a local migration operation before any sign-in;
    // server merging preserves the larger lifetime total without refunds.
    const p = JSON.parse(localStorage.getItem('bg9.prefs.v1') || '{}');
    const legacy = { salvage: p.salvage ?? 0, unlocks: p.unlocks ?? [], bestRoom: p.bestExpedition ?? 0,
      runs: p.expeditionRuns ?? 0, clears: p.expeditionClears ?? 0 };
    const restored = new Progression(localStorage, legacy, () => {}, message => { throw new Error(message); }).value();
    for (const key of Object.keys(localStorage).filter(k => k.startsWith(PROGRESS_PREFIX))) localStorage.removeItem(key);
    new Progression(localStorage, restored, () => {}, message => { throw new Error(message); });
    status.textContent = 'Save restored. Open the game below to check your run and progress.';
    el<HTMLButtonElement>('import').disabled = true;
  } catch {
    try {
      for (const key of Object.keys(localEntries())) localStorage.removeItem(key);
      for (const [key, value] of Object.entries(previous.entries)) localStorage.setItem(key, value);
    } catch { /* The downloaded and retained backup can be imported again. */ }
    status.textContent = 'Restore failed. Your previous save was kept in the before-restore backup. Free browser storage and try again.';
  }
};

if (!canonical && 'serviceWorker' in navigator) {
  void navigator.serviceWorker.getRegistrations().then(rs => Promise.all(rs.map(r => r.unregister()))).catch(() => {});
}
